import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';

import {
  AppCheckbox,
  AppForm,
  AppFormLabel,
  AppTextArea,
  AppTextField,
} from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import useAppToast from '@/hooks/useAppToast';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { checkinMailFormSchema, type CheckinMailFormSchema } from '@/schemas/checkinMailSchema';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { useTheme } from '@/shared/providers/theme/ThemeContext';

const defaultFormValues: CheckinMailFormSchema = {
  email: '',
  is_status: false,
  add_sentence: '',
  remarks: '',
};

interface PatientCheckinMailFormProps {
  formId: string;
  mode: 'create' | 'edit';
  patientName?: string;
  disabled?: boolean;
  defaultValues?: Partial<CheckinMailFormSchema>;
  onSubmit: (data: CheckinMailFormSchema) => void;
}

const PatientCheckinMailForm: React.FC<PatientCheckinMailFormProps> = ({
  formId,
  mode,
  patientName = '',
  disabled = false,
  defaultValues,
  onSubmit,
}) => {
  const toast = useAppToast();
  const { serviceType } = useTheme();

  const isA2 = serviceType === SERVICE_TYPE_A2;
  const treatmentType = isA2 ? 'はり・きゅう施術' : 'マッサージ施術';

  const initialValues = useMemo<CheckinMailFormSchema>(
    () => ({ ...defaultFormValues, ...defaultValues }),
    [defaultValues]
  );

  const { control, handleSubmit, watch } = useTrimmedForm<CheckinMailFormSchema>({
    resolver: zodResolver(checkinMailFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialValues,
  });

  const currentValues = watch();
  useFormChangeGuard(currentValues, initialValues);

  return (
    <CollapsibleCard title={mode === 'edit' ? 'CI通知（編集）' : 'CI通知（新規作成）'}>
      <form
        id={formId}
        onSubmit={handleSubmit(onSubmit, () => {
          toast.error('更新ができませんでした。');
        })}
      >
        <div className="grid grid-cols-[240px_1fr] gap-x-6 gap-y-4 items-start">
          {/* Email */}
          <AppFormLabel label="Email" required />
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppForm.Item
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
                className="mb-0"
              >
                <AppTextField
                  {...field}
                  className="w-full"
                  placeholder="example@example.com"
                  maxLength={255}
                  status={error ? 'error' : undefined}
                  disabled={disabled}
                />
              </AppForm.Item>
            )}
          />

          {/* 状態 */}
          <AppFormLabel label="状態" />
          <Controller
            name="is_status"
            control={control}
            render={({ field }) => (
              <AppCheckbox
                checked={!!field.value}
                onChange={e => field.onChange(e.target.checked)}
                disabled={disabled}
              >
                有効
              </AppCheckbox>
            )}
          />

          {/* 送信文 */}
          <AppFormLabel label="送信文" />
          <div>
            <div className="rounded-md border border-gray-300 p-3">
              <div className="text-sm text-gray-800">
                <div>
                  {patientName}様の{treatmentType}を開始します。
                </div>
                <div>施術時間は●●分です。</div>
              </div>
              <div className="mt-3">
                <Controller
                  name="add_sentence"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <AppTextArea
                      {...field}
                      rows={3}
                      maxLength={250}
                      placeholder="治療院名の上に挿入する文章（最大250文字）"
                      disabled={disabled}
                      status={error ? 'error' : undefined}
                    />
                  )}
                />
              </div>
              <div className="mt-3 text-center text-sm text-gray-800">●●●治療院</div>
              <div className="mt-2 text-sm text-gray-600">
                <div>※このメールは送信専用のアドレスで送信されています。</div>
                <div>　このメールに返信されても、返信内容の確認およびご返答ができません。</div>
                <div>　あらかじめご了承ください。</div>
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              <div>※施術時間と治療院名は実際の情報で送信されます。</div>
              <div>※治療院名の上に挿入できる文章は最大250文字までとなります。</div>
            </div>
          </div>

          {/* 備考 */}
          <AppFormLabel label="備考" />
          <Controller
            name="remarks"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppTextArea
                {...field}
                value={field.value ?? ''}
                rows={3}
                disabled={disabled}
                status={error ? 'error' : undefined}
                maxLength={255}
              />
            )}
          />
        </div>
      </form>
    </CollapsibleCard>
  );
};

export default PatientCheckinMailForm;
